// src/components/PhoneCapture/PhoneCaptureButton.jsx
//
// The one control that turns any desktop upload field into "photograph it with
// your phone". Renders a button; the modal behind it holds a QR code the staff
// member scans, and photos appear in the form as they are taken.

import React, { useCallback, useMemo, useState } from 'react';
import { Button, Modal, Typography, Space, Alert, Spin, Tag, Empty } from 'antd';
import { QrcodeOutlined, MobileOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { QRCodeSVG } from 'qrcode.react';
import usePhoneCaptureSession from './usePhoneCaptureSession';
import { captureBaseIsLocal } from '../../services/captureApi';

const { Text, Paragraph } = Typography;

/**
 * @param {string}   targetKey    stable id, e.g. 'walkInCase.goldImages'
 * @param {string}   targetLabel  shown on the phone, e.g. 'Gold Images'
 * @param {string}   folderPath   ImageKit folder to upload into
 * @param {Function} onFiles      (files[]) => void — called as photos arrive
 * @param {string}   [recordId]
 * @param {string}   [slot]
 * @param {number}   [maxFiles=10]
 * @param {boolean}  [requireGeo=false]
 * @param {string}   [accept='image/*']
 * @param {boolean}  [disabled]
 * @param {string}   [buttonText='Capture from Phone']
 * @param {Object}   [buttonProps]  passed through to the antd Button
 */
const PhoneCaptureButton = ({
  targetKey,
  targetLabel,
  folderPath,
  onFiles,
  recordId,
  slot,
  maxFiles = 10,
  requireGeo = false,
  accept = 'image/*',
  disabled = false,
  buttonText = 'Capture from Phone',
  buttonProps = {},
}) => {
  const [open, setOpen] = useState(false);

  const config = useMemo(
    () => ({ targetKey, targetLabel, folderPath, recordId, slot, maxFiles, requireGeo, accept }),
    [targetKey, targetLabel, folderPath, recordId, slot, maxFiles, requireGeo, accept],
  );

  const handleFiles = useCallback((incoming) => { onFiles?.(incoming); }, [onFiles]);

  const { open: openSession, close, url, files, loading, error, session } =
    usePhoneCaptureSession(config, handleFiles);

  const handleOpen = async () => {
    setOpen(true);
    try {
      await openSession();
    } catch {
      // error state renders inside the modal
    }
  };

  const handleClose = async () => {
    setOpen(false);
    await close();
  };

  const localHost = captureBaseIsLocal();

  return (
    <>
      <Button
        icon={<QrcodeOutlined />}
        onClick={handleOpen}
        disabled={disabled}
        {...buttonProps}
      >
        {buttonText}
      </Button>

      <Modal
        title={<Space><MobileOutlined />Capture from Phone</Space>}
        open={open}
        onCancel={handleClose}
        footer={[<Button key="done" type="primary" onClick={handleClose}>Done</Button>]}
        width={420}
        destroyOnClose
      >
        {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}

        {localHost && (
          <Alert
            type="warning"
            showIcon
            message="This QR points at localhost"
            description="A phone cannot open a localhost address. Set REACT_APP_CAPTURE_BASE_URL to the CRM's public frontend URL for this to work off the desktop."
            style={{ marginBottom: 12 }}
          />
        )}

        <Paragraph style={{ marginBottom: 12 }}>
          Scan this code with your phone camera, then take the photos.
          They appear here automatically — keep this window open.
        </Paragraph>

        <div style={{ textAlign: 'center', padding: 12, background: '#fff', borderRadius: 8 }}>
          {loading || !url ? (
            <Spin />
          ) : (
            <QRCodeSVG value={url} size={200} includeMargin level="M" />
          )}
        </div>

        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <Space direction="vertical" size={4}>
            <Text strong>{targetLabel}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Up to {maxFiles} file{maxFiles > 1 ? 's' : ''}
              {requireGeo ? ' · location required' : ''}
              {session?.expiresAt
                ? ` · link valid until ${new Date(session.expiresAt).toLocaleTimeString()}`
                : ''}
            </Text>
          </Space>
        </div>

        <div style={{ marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
          {files.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={<Text type="secondary">Waiting for photos from the phone…</Text>}
              style={{ margin: 0 }}
            />
          ) : (
            <>
              <Tag color="green" icon={<CheckCircleOutlined />}>
                {files.length} received
              </Tag>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                {files.map((f) => (
                  <img
                    key={f.fileId}
                    src={f.thumbnailUrl || f.url}
                    alt={f.name || 'Captured'}
                    style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6, border: '1px solid #d9d9d9' }}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </Modal>
    </>
  );
};

export default PhoneCaptureButton;
